import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Calendar,
  Camera,
  Image,
  X,
  ChevronLeft,
  Check,
  Clock,
  FileText
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MobileEvents = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  
  const [formData, setFormData] = useState({
    title: "",
    date: new Date().toISOString().split("T")[0],
    event_type: "participation",
    notes: ""
  });

  useEffect(() => {
    fetchEvents();
    // Check if we should open dialog immediately (from home quick action)
    if (location.state?.action === "add") {
      setDialogOpen(true);
      // Clear the state so it doesn't reopen on navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      // Show only recent events (last 7 days)
      const recentEvents = response.data
        .filter(e => {
          const eventDate = new Date(e.date);
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return eventDate >= weekAgo;
        })
        .slice(0, 10);
      setEvents(recentEvents);
    } catch (error) {
      console.error("Failed to fetch events:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setPhotoPreview(e.target?.result);
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    setPhotoFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    setSubmitting(true);
    try {
      // Create the event
      const eventPayload = {
        title: formData.title,
        description: formData.notes || "",
        event_type: formData.event_type,
        date: formData.date,
        time: "",
        location: "",
        participants_count: 0,
        highlights: "",
        notes: formData.notes || "",
        role: "lead",
        priority: "medium",
        follow_up_status: "open",
        category_tags: [],
        linked_goals: [],
        production_items: []
      };

      const response = await axios.post(`${API}/events`, eventPayload);
      const eventId = response.data.id;

      // Upload photo if present
      if (photoFile && eventId) {
        const photoFormData = new FormData();
        photoFormData.append("file", photoFile);
        await axios.post(`${API}/events/${eventId}/files`, photoFormData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      toast.success("Event added!");
      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error("Failed to add event");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      date: new Date().toISOString().split("T")[0],
      event_type: "participation",
      notes: ""
    });
    removePhoto();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div className="px-4 py-4" data-testid="mobile-events">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/mobile")}
            className="h-9 w-9"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-heading font-semibold">Events</h1>
            <p className="text-xs text-muted-foreground">Recent activity</p>
          </div>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          size="sm"
          className="bg-gradient-to-r from-blue-500 to-indigo-600 shadow-md"
          data-testid="mobile-add-event-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>

      {/* Events List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : events.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Calendar className="h-12 w-12 text-muted-foreground mb-3" strokeWidth={1} />
            <p className="font-medium">No recent events</p>
            <p className="text-sm text-muted-foreground mt-1">
              Tap "Add" to log your first event
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {events.map((event) => (
            <Card key={event.id} className="overflow-hidden" data-testid={`mobile-event-${event.id}`}>
              <div className={`h-1 ${event.event_type === "participation" ? "bg-blue-500" : "bg-amber-500"}`} />
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm line-clamp-2">{event.title}</h3>
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatDate(event.date)}
                      <Badge variant="outline" className="text-xs capitalize">
                        {event.event_type}
                      </Badge>
                    </div>
                  </div>
                  {event.files?.length > 0 && (
                    <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center">
                      <Image className="h-5 w-5 text-stone-400" />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Quick Add Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-[95vw] rounded-2xl p-0 gap-0">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="text-lg font-heading">Quick Add Event</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Title */}
            <div>
              <Label htmlFor="title" className="text-sm">What happened? *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Story time with toddlers"
                className="mt-1.5"
                autoFocus
                data-testid="mobile-event-title"
              />
            </div>

            {/* Date & Type Row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="date" className="text-sm">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="mt-1.5"
                  data-testid="mobile-event-date"
                />
              </div>
              <div>
                <Label htmlFor="type" className="text-sm">Type</Label>
                <Select
                  value={formData.event_type}
                  onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                >
                  <SelectTrigger className="mt-1.5" data-testid="mobile-event-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="participation">Participation</SelectItem>
                    <SelectItem value="noteworthy">Noteworthy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes" className="text-sm">Notes (optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional details..."
                rows={2}
                className="mt-1.5 resize-none"
                data-testid="mobile-event-notes"
              />
            </div>

            {/* Photo Upload */}
            <div>
              <Label className="text-sm">Photo (optional)</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoCapture}
                className="hidden"
                data-testid="mobile-event-photo-input"
              />
              
              {photoPreview ? (
                <div className="mt-2 relative">
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 h-8 w-8 rounded-full"
                    onClick={removePhoto}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-20 flex-col gap-2"
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.setAttribute("capture", "environment");
                        fileInputRef.current.click();
                      }
                    }}
                    data-testid="mobile-take-photo-btn"
                  >
                    <Camera className="h-5 w-5" />
                    <span className="text-xs">Take Photo</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-20 flex-col gap-2"
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.removeAttribute("capture");
                        fileInputRef.current.click();
                      }
                    }}
                    data-testid="mobile-upload-photo-btn"
                  >
                    <Image className="h-5 w-5" />
                    <span className="text-xs">Upload</span>
                  </Button>
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600"
                disabled={submitting}
                data-testid="mobile-submit-event-btn"
              >
                {submitting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-1" /> Save
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
